import React from 'react';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';

import IOrder from '../../unityView/interfaces/IOrder';
import IParamScript from '../../database/interfaces/paramScript/IParamScript';
import { DatabaseContext } from '../../database/DatabaseHandler';
import {createCdnThumbnailUrl} from "../../../config/cdn";
import { shownSideTokens, unavailableSideFlags, ZERO_GUID } from "../../configurator/cards/edgeKeys";
import { UNAVAILABLE_EDGE_BORDER } from "../../configurator/common/EdgeBorderedAvatar";

import './../../../../../../../themes/uc_base_theme/assets/css/edge.css'

interface EdgingIconProps {
    productItem: IOrder;
    /**
     * A kosár-elem descriptor-a (a szülő szekrény dekór-szabályai). Ebből derül ki, hogy az
     * adott elem száliránya keresztirányú-e ("Cross"). Maga az elem (IOrder) erre nem alkalmas,
     * mert a PatternOriented mező "Egyenes" ÉS "Kereszt" esetén is true.
     */
    descriptor?: IParamScript[];
}

/** Icon showing the edging of a product */
const EdgingIcon: React.FC<EdgingIconProps> = (props) => {
    const dataContext = React.useContext(DatabaseContext);
    const edges = props.productItem.Edges;
    const board = dataContext.boardData?.findByID(props.productItem.ProductID);
    const thumbnailUrl: string = createCdnThumbnailUrl(board?.ProductImage);
    const isValidEdge = (edge: any) => edge && edge.ProductID !== ZERO_GUID;
    const avatarSize = 60;

    // Az elemhez tartozó Decor-szabály a szekrény descriptor-ából (az elem board-ja alapján). Ebből jön a
    // szálirány ÉS az élzárható oldalak halmaza is — ugyanabból a forrásból, mint a konfigurátorban.
    const decorRule = (props.descriptor ?? []).find((rule) =>
        rule.parameterType === "Decor" &&
        rule.parameterValue &&
        typeof rule.parameterValue === "object" &&
        rule.parameterValue.BoardProduct === props.productItem.ProductID
    );

    // Keresztirányú szálirány esetén ugyanúgy 90°-kal elforgatjuk a thumbnail mintázatát,
    // ahogy a BoardContent (EdgeBorderedAvatar) teszi.
    const rotated = board?.Oriented === 1 && decorRule?.parameterValue?.BoardPatternDirection === "Cross";

    // Az élzárható oldalak: a Decor-descriptor `edge.sideLabels` kulcsaiból — pontosan úgy, ahogy a
    // konfigurátor is számolja (shownSideTokens), különben a kosár-ikon MÁS oldalt jelölne, mint a
    // konfigurátor thumbnailje. Descriptor nélkül a régi, IsWorkTop-alapú szabályra esünk vissza.
    const allowedSides = shownSideTokens((decorRule?.typeDescriptor as any)?.edge?.sideLabels, !!board?.IsWorkTop);
    // A nem élzárható oldal (munkalapnál a front: ott nincs Top) itt is szaggatott kerettel látszik.
    const unavailable = unavailableSideFlags(allowedSides);
    const borderStyles = {
        ...(unavailable.top && { borderTop: UNAVAILABLE_EDGE_BORDER }),
        ...(unavailable.right && { borderRight: UNAVAILABLE_EDGE_BORDER }),
        ...(unavailable.bottom && { borderBottom: UNAVAILABLE_EDGE_BORDER }),
        ...(unavailable.left && { borderLeft: UNAVAILABLE_EDGE_BORDER }),
        ...(allowedSides.includes("top") && isValidEdge(edges?.Top) && { borderTop: "5px solid white" }),
        ...(allowedSides.includes("right") && isValidEdge(edges?.Right) && { borderRight: "5px solid white" }),
        ...(allowedSides.includes("bottom") && isValidEdge(edges?.Bottom) && { borderBottom: "5px solid white" }),
        ...(allowedSides.includes("left") && isValidEdge(edges?.Left) && { borderLeft: "5px solid white" }),
    };

    return (edges && (
        <Box marginRight={5} flexBasis={avatarSize} flexShrink={1} position="relative" width={avatarSize} height={avatarSize}>
            <Avatar
                    className={"hasEdgeBorder"}
                    children=" "
                    alt={"X"}
                    src={thumbnailUrl}
                    variant="rounded"
                    sx={{
                        width: avatarSize,
                        height: avatarSize,
                        // Csak a mintázatot (a belső img-et) forgatjuk; az él-borderek a
                        // ::after-en maradnak, így a helyükön rögzülnek.
                        '& img': { transform: rotated ? 'rotate(-90deg)' : 'none' },
                        '&::after': { ...borderStyles },
                    }}
                />
        </Box>
    ));
}

export default EdgingIcon;
